import { useState } from 'react'

import { api } from '../api/client'
import type { FirewallTraffic } from '../api/types'
import { Card, EmptyState, ErrorBanner, Spinner } from '../components/common'
import { DataTable } from '../components/DataTable'
import { FirewallDashboard, nomeFornitore } from '../components/FirewallDashboard'
import { FirewallFlowsTable } from '../components/FirewallFlows'
import { useAsync } from '../hooks/useAsync'
import { Connessioni } from '../components/Connessioni'
import { t } from '../i18n/lingua'

/**
 * La pagina «Firewall» (Sicurezza): l'elenco dei firewall configurati (Palo Alto
 * e FortiGate, che convivono: stesse viste, il fornitore in colonna),
 * il **cruscotto** del firewall scelto (sistema, sessioni, VPN, minacce, URL,
 * accessi falliti) e un **Log Query tool** per interrogarne uno su un IP sorgente
 * e/o destinazione — tutto in tempo reale attraverso la sonda. Il cruscotto è del
 * firewall, non di un CI: per questo vive qui.
 */
export default function Firewall() {
  const elenco = useAsync(() => api.firewalls(), [])
  const probes = useAsync(() => api.probes(), [])
  const firewalls = elenco.data?.firewalls ?? []
  const perSonda = new Map((probes.data ?? []).map((p) => [p.id, p.name] as [string, string]))
  const [scelto, setScelto] = useState<string | null>(null)
  const attivo = scelto ?? (firewalls.length === 1 ? firewalls[0].id : null)

  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">{t('Firewall')}</h1>
          <p className="page__subtitle">
            {t('Firewall configurati e interrogazione in tempo reale dei relativi log tramite sonda.')}
          </p>
        </div>
      </header>

      <ErrorBanner error={elenco.error} />

      <Card title={t('Firewall configurati')}>
        {elenco.loading && !elenco.data ? (
          <Spinner />
        ) : !firewalls.length ? (
          <EmptyState
            title={t('Nessun firewall configurato')}
            hint={t('Aggiungi un firewall «Palo Alto (firewall)» o «Fortinet FortiGate (firewall)» qui sotto, in «Connessioni».')}
          />
        ) : (
          <DataTable
            rows={firewalls}
            initialSort={{ key: 'name' }}
            rowKey={(f) => f.id}
            columns={[
              { key: 'name', header: t('Firewall'), value: (f) => f.name },
              { key: 'vendor', header: t('Fornitore'), value: (f) => nomeFornitore(f.vendor) },
              {
                key: 'sonda', header: t('Sonda'),
                value: (f) => (f.appliance_id ? perSonda.get(f.appliance_id) ?? f.appliance_id : ''),
                render: (f) =>
                  f.appliance_id ? (perSonda.get(f.appliance_id) ?? f.appliance_id) : t('La prima idonea'),
              },
            ]}
          />
        )}
      </Card>

      {firewalls.length > 0 && (
        <Card
          title={t('Cruscotto')}
          actions={
            firewalls.length > 1 ? (
              <label className="field" style={{ margin: 0 }}>
                <span className="field__label">{t('Firewall')}</span>
                <select value={attivo ?? ''} onChange={(e) => setScelto(e.target.value || null)}>
                  <option value="">{t('Scegli un firewall…')}</option>
                  {firewalls.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name} · {nomeFornitore(f.vendor)}
                    </option>
                  ))}
                </select>
              </label>
            ) : null
          }
        >
          {attivo ? (
            <p className="field__hint">{t('Il cruscotto del firewall selezionato, in tempo reale via sonda.')}</p>
          ) : (
            <p className="muted">{t('Scegli un firewall qui sopra per vederne il cruscotto.')}</p>
          )}
        </Card>
      )}

      {attivo && <FirewallDashboard firewallId={attivo} />}

      <LogQueryTool firewalls={firewalls} />
      <h2 className="sezione-pagina__titolo" id="connessioni">{t('Connessioni')}</h2>
      <Connessioni funzioni={['firewall']} segnaposto="firewall-sede" onChanged={() => elenco.reload()} />
    </>
  )
}

/** Il Log Query tool: IP sorgente e/o destinazione + firewall → traffico. */
function LogQueryTool({
  firewalls,
}: {
  firewalls: { id: string; name: string; vendor?: string; appliance_id: string | null }[]
}) {
  const [src, setSrc] = useState('')
  const [dst, setDst] = useState('')
  const [blocked, setBlocked] = useState(false)
  const [firewallId, setFirewallId] = useState('')
  const [dati, setDati] = useState<FirewallTraffic | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  const piuFirewall = firewalls.length > 1

  async function interroga() {
    setBusy(true)
    setError(null)
    try {
      const r = await api.firewallQuery({
        firewallId: firewallId || undefined,
        src: src.trim() || undefined,
        dst: dst.trim() || undefined,
        blocked,
      })
      setDati(r)
    } catch (e) {
      setError(e)
    } finally {
      setBusy(false)
    }
  }

  const puòInterrogare = (src.trim() || dst.trim()) && !busy && firewalls.length > 0

  return (
    <Card title={t('Log Query')}>
      <p className="field__hint">
        {t('Interroga un firewall per IP sorgente e/o destinazione e mostra il traffico rilevato, consentito o bloccato. L’opzione «Solo bloccato» limita i risultati al traffico negato.')}
      </p>
      <div className="ci-sommario" style={{ gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <label className="field" style={{ margin: 0 }}>
          <span className="field__label">{t('IP sorgente')}</span>
          <input value={src} onChange={(e) => setSrc(e.target.value)} placeholder="10.0.0.5" />
        </label>
        <label className="field" style={{ margin: 0 }}>
          <span className="field__label">{t('IP destinazione')}</span>
          <input value={dst} onChange={(e) => setDst(e.target.value)} placeholder="8.8.8.8" />
        </label>
        {piuFirewall && (
          <label className="field" style={{ margin: 0 }}>
            <span className="field__label">{t('Firewall')}</span>
            <select value={firewallId} onChange={(e) => setFirewallId(e.target.value)}>
              <option value="">{t('Scegli un firewall…')}</option>
              {firewalls.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name} · {nomeFornitore(f.vendor)}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="checkbox" style={{ margin: 0 }}>
          <input type="checkbox" checked={blocked} onChange={(e) => setBlocked(e.target.checked)} />
          {t('Solo bloccato')}
        </label>
        <button onClick={interroga} disabled={!puòInterrogare}>
          {busy ? t('Interrogo…') : t('Interroga')}
        </button>
      </div>

      <ErrorBanner error={error} />
      {busy && <Spinner label={t('Interrogo il firewall…')} />}
      {!busy && piuFirewall && !firewallId && dati?.needs_choice && (
        <p className="muted">{t('Scegli quale firewall interrogare.')}</p>
      )}
      {!busy && dati && !dati.available && dati.reason && <p className="muted">{dati.reason}</p>}
      {!busy && dati && dati.available && !dati.needs_choice && (
        (dati.flows ?? []).length === 0 ? (
          <EmptyState
            title={t('Nessun traffico')}
            hint={t('Il firewall non ha righe recenti per gli IP indicati.')}
          />
        ) : (
          <FirewallFlowsTable flows={dati.flows ?? []} />
        )
      )}
    </Card>
  )
}
